/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BookListReturnParams } from '../models/BookListReturnParams';
import type { CompleteBookReturn } from '../models/CompleteBookReturn';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class BooksService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * List all the books based on the given search params.
     * List retrieved from the project Gutenberg API.
     *
     * These params are taken from project Gutenberg to match their available queries.
     * https://gutendex.com/
     * @param topic Use this to search for a case-insensitive key-phrase in books' bookshelves or subjects. For example, /books?topic=children gives books on the "Children's Literature" bookshelf, with the subject "Sick children -- Fiction", and so on.
     * @param search Use this to search author names and book titles with given words. They must be separated by a space (i.e. %20 in URL-encoded format) and are case-insensitive. For example, /books?search=dickens%20great includes Great Expectations by Charles Dickens.
     * @param languages Use this to find books in any of a list of languages. They must be comma-separated, two-character language codes. For example, /books?languages=en gives books in English, and /books?languages=fr,fi gives books in either French or Finnish or both.
     * @param page the page number of query (default to 1)
     * @returns BookListReturnParams list of books
     * @throws ApiError
     */
    public listBooks(
        topic?: string,
        search?: string,
        languages?: Array<string>,
        page?: number,
    ): CancelablePromise<BookListReturnParams> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/books',
            query: {
                'topic': topic,
                'search': search,
                'languages': languages,
                'page': page,
            },
        });
    }

    /**
     * Get a book by id.
     * If the book is in the db, return it.
     * If the book is not in the db, retrieve it from project gutenburg, and add it to the db.
     * @param bookId the id of the given book to be returned
     * @returns CompleteBookReturn Ok
     * @throws ApiError
     */
    public getBook(
        bookId: number,
    ): CancelablePromise<CompleteBookReturn> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/books/{bookId}',
            path: {
                'bookId': bookId,
            },
        });
    }

    /**
     * Add a rating or a comment to the book with the given id.
     * If the book is in the db, throw an error.
     * Otherwise, add the provided rating or comment to the list of ratings or comments
     * @param requestBody the updated book
     * @returns void
     * @throws ApiError
     */
    public updateBook(
        requestBody: CompleteBookReturn,
    ): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/books/{bookId}',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Get a book by id.
     * If the book is in the db, return it.
     * If the book is not in the db, retrieve it from project gutenburg, and add it to the db.
     * @param bookId the id of the given book to be returned
     * @returns string Ok
     * @throws ApiError
     */
    public getBookText(
        bookId: number,
    ): CancelablePromise<string> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/books/{bookId}/text',
            path: {
                'bookId': bookId,
            },
        });
    }

}
